'use client';

import { Tooltip } from '@packages/modules/src/Tooltip/Tooltip';
import { ConditionalWrapper } from '@packages/shared/src/components/ConditionalWrapper/ConditionalWrapper';
import { useConfig } from '@packages/shared/src/hooks/useConfig/useConfig';
import { useMediaType } from '@packages/shared/src/hooks/useMediaType/useMediaType';
import Image from 'next/image';
import { type ReactNode } from 'react';
import { type Positioning } from '../../../interfaces';
import type { CmsImageType } from '../../components/CmsImage/types';
import { useGetTooltipContent } from '../../hooks/useGetTooltipContent';

export type BannerItemImageProps = {
  data: CmsImageType;
};

const TooltipWrapper =
  (tooltipContent?: string, tooltipPositioning?: Positioning) => (ch: ReactNode) => (
    <Tooltip content={tooltipContent || undefined} positioning={tooltipPositioning}>
      {ch}
    </Tooltip>
  );

export const BannerItemImage = ({
  data: {
    field_alternate_text: imageAlt,
    field_image_default: imageDefault,
    field_image_mobile: imageMobile,
    promotion_item: tooltipDataPromotion,
    field_content_snippet_tooltip: tooltipDataContentSnippet,
    behavior_paragraph_tooltip_activation: tooltipActivation,
    behavior_paragraph_tooltip_positioning: tooltipPositioning,
  },
}: BannerItemImageProps) => {
  const isDesktop = useMediaType();
  const {
    assets: {
      cmsImage: {
        imageOptimizationEnabled,
        uriTransformSource,
        uriTransformEndpoint,
        imageQuality,
      },
    },
  } = useConfig();

  const tooltipContent = useGetTooltipContent(tooltipDataPromotion, tooltipDataContentSnippet);

  const imageData = [!isDesktop && imageMobile, imageDefault].find(
    (image) => image && typeof image.uri === 'object' && image.uri?.url,
  );

  if (!imageData) {
    return null;
  }

  const { width: imageDataWidth, height: imageDataHeight } = imageData?.meta || {};
  const { url: imageUri = '', value: imageValue = null } =
    (typeof imageData?.uri === 'object' && imageData.uri) || {};

  return (
    <ConditionalWrapper
      condition={!!(tooltipContent && !!tooltipActivation)}
      wrapper={TooltipWrapper(tooltipContent, tooltipPositioning)}
    >
      {imageValue && imageDataWidth && imageDataHeight ? (
        <Image
          src={
            imageOptimizationEnabled
              ? imageValue.replace(uriTransformSource, uriTransformEndpoint)
              : imageUri
          }
          alt={imageAlt || ''}
          width={typeof imageDataWidth === 'number' ? imageDataWidth : parseInt(imageDataWidth, 10)}
          height={
            typeof imageDataHeight === 'number' ? imageDataHeight : parseInt(imageDataHeight, 10)
          }
          loading="lazy"
          {...(imageOptimizationEnabled && { quality: imageQuality })}
          {...(!imageOptimizationEnabled && { unoptimized: true })}
        />
      ) : (
        <img src={imageUri} alt={imageAlt || ''} loading="lazy" />
      )}
    </ConditionalWrapper>
  );
};
